import { inject, Injectable, signal } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpClient
} from '@angular/common/http';
import { Observable} from 'rxjs';
import {  shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface AuthCreds {
    csrfToken: string;
    sessionId: string;
}

@Injectable()
export class BffAuthInterceptor implements HttpInterceptor {
    private readonly http = inject(HttpClient);
    private authCreds = signal<AuthCreds | null>(null);
    fetchingCreds$: Observable<AuthCreds> | null = null;

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {

        // Solo agregar auth headers para requests al BFF
        if (!this.isBffRequest(request.url)) {
            return next.handle(request);
        }

        // Si no es mutativa -> continuar normal
        if (!this.isMutating(request.method)) {
            return next.handle(request);
        }


        // 2 Si ya tengo credenciales -> agregar headers
        const creds = this.authCreds();

        if (creds) {
            const authRequest = this.addAuthHeaders(request);
            return next.handle(authRequest);
        }

        // 3 Si no tengo credenciales -> pedirlas primero
        return this.getAuthCreds().pipe(
            switchMap(() => {
                const authRequest = this.addAuthHeaders(request);
                return next.handle(authRequest);
            })
        );
    }

    private isMutating(method: string): boolean {
        return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    }

    private isBffRequest(url: string): boolean {
        return url.startsWith(environment.bffUrl);
    }

    private addAuthHeaders(request: HttpRequest<unknown>): HttpRequest<unknown> {
        const creds = this.authCreds();
        if (!creds) return request;

        return request.clone({
            setHeaders: {
                'x-csrf-Token': creds.csrfToken,
                'x-session-id': creds.sessionId
            }
        });
    }

    private getAuthCreds(): Observable<AuthCreds> {

        // evita múltiples llamadas concurrentes
        if (!this.fetchingCreds$) {
            this.fetchingCreds$ = this.http
                .get<AuthCreds>(`${environment.bffUrl}/auth/csrf-token`)
                .pipe(
                    tap((creds) => {
                        this.authCreds.set(creds);
                    }),
                    shareReplay(1)
                );
        }

        return this.fetchingCreds$;
    }

}
